import { useState, useEffect, useCallback, useRef } from "react";

/**
 * useInfiniteScroll Hook
 *
 * @param {Function} fetchFunction - Async (page) => { ok, data: { results, count } }
 * @param {Array} dependencies - Reset + reload when these change
 * @param {Number} pageSize - Unused historically; kept for call-site compatibility
 * @param {Boolean} enabled - When false, keep cached data and do not fetch
 */
export const useInfiniteScroll = (
  fetchFunction,
  dependencies = [],
  pageSize = 10,
  enabled = true
) => {
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [shouldLoad, setShouldLoad] = useState(true);
  const isLoadingRef = useRef(false);
  const pageRef = useRef(1);
  const hasMoreRef = useRef(true);

  const reset = useCallback(() => {
    setData([]);
    setPage(1);
    pageRef.current = 1;
    setHasMore(true);
    hasMoreRef.current = true;
    setError(null);
    setTotalCount(0);
    setShouldLoad(true);
    isLoadingRef.current = false;
  }, []);

  const loadMore = useCallback(async () => {
    if (!enabled || isLoadingRef.current || !hasMoreRef.current) return;

    const currentPage = pageRef.current;

    try {
      isLoadingRef.current = true;
      setLoading(true);
      setError(null);

      const result = await fetchFunction(currentPage);

      if (result?.ok && result?.data) {
        const newResults = result.data.results || [];
        const count = result.data.count || 0;

        setData((prevData) => {
          const existingIds = new Set(prevData.map((item) => item?.id));
          const uniqueNew = newResults.filter(
            (item) => item?.id == null || !existingIds.has(item.id)
          );
          const updatedData = [...prevData, ...uniqueNew];
          const moreAvailable =
            newResults.length > 0 && updatedData.length < count;

          hasMoreRef.current = moreAvailable;
          setHasMore(moreAvailable);

          return updatedData;
        });

        pageRef.current = currentPage + 1;
        setPage(currentPage + 1);
        setTotalCount(count);
      } else {
        hasMoreRef.current = false;
        setHasMore(false);
      }
    } catch (err) {
      setError(err);
      hasMoreRef.current = false;
      setHasMore(false);
      console.error("Error loading data:", err);
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  }, [fetchFunction, enabled]);

  const refetch = useCallback(() => {
    reset();
  }, [reset]);

  // Reset when dependencies change (search, folder, sort, etc.)
  useEffect(() => {
    reset();
  }, dependencies); // eslint-disable-line react-hooks/exhaustive-deps

  // Load only while enabled; keep cache when disabled (e.g. other tab)
  useEffect(() => {
    if (!enabled) return;
    if (
      shouldLoad &&
      data.length === 0 &&
      hasMore &&
      !loading &&
      !isLoadingRef.current
    ) {
      setShouldLoad(false);
      loadMore();
    }
  }, [data.length, shouldLoad, hasMore, loading, enabled, loadMore]);

  return {
    data,
    loading,
    hasMore,
    error,
    totalCount,
    reset,
    refetch,
    loadMore,
  };
};
